import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/api";
import { ArrowUpRight, ArrowRight, MessageCircle } from "lucide-react";
import Seo, { organizationJsonLd } from "@/components/Seo";

const LEARN_URL = "https://learn.ravemora.com/";
const WA_HOME = `https://wa.me/917483500550?text=${encodeURIComponent(
  "Hi Ravemora, I'd like to know more about Ravemora Global and the services you offer. Please guide me on the right option."
)}`;

const FOUNDER_PHOTO = "/founder-raghavendra.jpg";

const services = [
  {
    n: "01",
    title: "Academic and Career Counselling",
    line: "Make informed decisions about the future.",
    href: "https://learn.ravemora.com/careercoach",
    external: true,
  },
  {
    n: "02",
    title: "Coaching and Mentoring",
    line: "Develop capability through experience.",
    href: LEARN_URL,
    external: true,
  },
  {
    n: "03",
    title: "Fresher and Talent Recruitment",
    line: "Connect emerging talent with opportunity.",
    href: "/recruitment",
    external: false,
  },
  {
    n: "04",
    title: "Payroll and Workforce Support",
    line: "Support the workforce behind the business.",
    href: "/payroll",
    external: false,
  },
];

const steps = [
  { n: "01", title: "Discover", line: "Understand interests, strengths, needs and potential." },
  { n: "02", title: "Develop", line: "Build practical skills, knowledge and professional capability." },
  { n: "03", title: "Connect", line: "Create meaningful connections between people, talent and opportunity." },
  { n: "04", title: "Transform", line: "Help individuals and organizations turn capability into lasting value." },
];

export default function Home() {
  return (
    <div>
      <Seo
        path="/"
        exactTitle="Ravemora Global | Human Capital Transformation"
        image="https://ravemora.com/og/og-home.jpg"
        description="Ravemora Global helps individuals and organizations grow through learning, recruitment, payroll and career solutions designed around real-world needs."
        jsonLd={organizationJsonLd()}
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6 lg:pt-10 pb-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-navy/60">Human Capital Transformation</div>
            <h1 className="serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
              Develop People. <br />
              Connect Talent. <br />
              Build <span className="text-gold">Stronger Workforces.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-navy/70 leading-relaxed">
              Ravemora Global helps individuals discover their potential, develop practical capabilities and connect with meaningful opportunities — while helping organizations build and support capable workforces.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => document.getElementById("what-we-do")?.scrollIntoView({ behavior: "smooth" })} className="btn-primary" data-testid="hero-explore-services"><ArrowRight size={14}/> Explore Our Services</button>
              <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="hero-learn-at-ravemora">Learn at Ravemora <ArrowUpRight size={14}/></a>
              <a href={WA_HOME} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="hero-whatsapp"><MessageCircle size={14}/> WhatsApp Us</a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-navy/10">
              <img src={IMAGES.heroOffice} alt="Corporate office" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="home-introduction">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-navy">
              Human Capital, <span className="text-gold">From Potential to Performance.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-navy/80">
            <p>Ravemora Global is a Human Capital Transformation company focused on developing people, connecting talent with opportunity and helping organizations build capable and adaptable workforces.</p>
            <p>We work across the human-capital journey — from helping individuals make informed academic and career decisions, to developing skills through coaching and mentoring, connecting fresh talent with organizations, and supporting businesses with payroll and workforce solutions.</p>
            <p>Our approach is simple: understand people, develop capability, create opportunity and support sustainable growth.</p>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section id="what-we-do" className="bg-navy text-offwhite relative overflow-hidden grain scroll-mt-24" data-testid="home-what-we-do">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-gold">What we do</div>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">Four ways we move people <span className="text-gold">forward.</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-16">
            {services.map((s) => (
              <div key={s.n} className="border border-gold/30 p-10 flex flex-col" data-testid={`home-service-${s.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{s.n}</div>
                <h3 className="serif-display text-3xl mt-6 leading-snug">{s.title}</h3>
                <p className="text-offwhite/70 mt-4 leading-relaxed flex-1">{s.line}</p>
                {s.external ? (
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 text-gold label-eyebrow" data-testid={`home-service-${s.n}-link`}>
                    Learn more <ArrowUpRight size={14}/>
                  </a>
                ) : (
                  <Link to={s.href} className="mt-8 inline-flex items-center gap-2 text-gold label-eyebrow" data-testid={`home-service-${s.n}-link`}>
                    Learn more <ArrowUpRight size={14}/>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="home-founder">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            {FOUNDER_PHOTO ? (
              <img src={FOUNDER_PHOTO} alt="Raghavendra Ramanand, Founder of Ravemora Global" className="w-full aspect-[4/5] object-cover border border-navy/10" data-testid="founder-photo" />
            ) : (
              <div className="w-full aspect-[4/5] border border-navy/10 bg-slateBg flex items-center justify-center" data-testid="founder-photo">
                <span className="serif-display text-8xl text-navy/20">RR</span>
              </div>
            )}
          </div>
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-navy/60">The Person Behind Ravemora</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Raghavendra Ramanand</h2>
            <div className="label-eyebrow text-gold mt-3">Founder, Ravemora Global</div>
            <div className="mt-10 space-y-6 text-lg leading-relaxed text-navy/80">
              <p>Ravemora Global was founded with a simple belief: people can achieve more when they have the right direction, the right knowledge and the right opportunities.</p>
              <p>With extensive experience across IT infrastructure, network and security operations, consulting and professional development, Raghavendra brings together industry experience and a passion for developing people.</p>
              <p>Through Ravemora, his focus is to build meaningful connections between experience, capability, talent and opportunity — helping individuals grow while helping organizations build stronger workforces.</p>
            </div>
            <blockquote className="mt-12 border-l-2 border-gold pl-8" data-testid="founder-closing">
              <p className="serif-display text-2xl leading-snug text-navy">"Building Ravemora Global around one belief: human potential can be transformed into meaningful capability and opportunity."</p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* FROM POTENTIAL TO IMPACT */}
      <section className="bg-slateBg py-24 lg:py-32" data-testid="home-impact">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="label-eyebrow text-navy/60">How we work</div>
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight text-navy">From Potential <span className="text-gold">to Impact.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14 mt-16">
            {steps.map((s) => (
              <div key={s.n} className="border-t border-navy pt-8" data-testid={`impact-step-${s.n}`}>
                <div className="serif-display text-gold text-5xl leading-none">{s.n}</div>
                <div className="serif-display text-2xl text-navy mt-6">{s.title}</div>
                <p className="text-navy/70 mt-3 leading-relaxed">{s.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="home-vision-mission">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div className="border-t border-gold pt-8" data-testid="home-vision">
              <div className="label-eyebrow text-gold">Vision</div>
              <p className="serif-display text-2xl md:text-3xl mt-6 leading-snug">Our vision is to transform human potential into meaningful capability, opportunity and organizational value.</p>
            </div>
            <div className="border-t border-gold pt-8" data-testid="home-mission">
              <div className="label-eyebrow text-gold">Mission</div>
              <p className="serif-display text-2xl md:text-3xl mt-6 leading-snug">Our mission is to discover potential, develop capability, connect people with meaningful opportunities and help organizations build stronger, more adaptable workforces through practical learning, talent solutions and workforce support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-offwhite" data-testid="home-final-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-10">
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-tight text-navy max-w-3xl">Let's Build What <span className="text-gold">People Can Become.</span></h2>
          <p className="mt-8 max-w-2xl text-lg text-navy/70 leading-relaxed">
            Whether you are looking to develop your capabilities, find emerging talent, strengthen your workforce or make an informed career decision, Ravemora Global can help.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="cta-learn-at-ravemora">Learn at Ravemora <ArrowUpRight size={14}/></a>
            <Link to="/recruitment" className="btn-primary" data-testid="cta-recruitment">Recruitment</Link>
            <Link to="/payroll" className="btn-primary" data-testid="cta-payroll">Payroll</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
